import { mapMutations, mapState, mapGetters, mapActions } from "vuex";
import formIoApi from "./formIoApi";
import formioHelpers from "./formioHelpers";
import ordersService from "@/services/orders";
import programSessionService from "@/services/programSessions";
import { useToast } from "vue-toast-notification";
import { ERROR_TIMEOUT } from "@/constants.js";

export default {
  mixins: [formIoApi, formioHelpers],
  inject: ["session"],
  data() {
    return {
      sessionMinimumGpa: undefined,
      toaster: useToast(),
    };
  },
  computed: {
    ...mapGetters(["getCurrentUser"]),
    ...mapState([
      "formioToken",
      "program",
      "studentApplications",
      "currentApplicationId",
      "profileData",
      "currentGpa",
    ]),
    formioGpa() {
      let gpa = 0;
      //find the form.io id of the academic profile
      let academicForm = this.forms.filter((form) => {
        return form.slug === "academic-profile";
      });
      //get students reported gpa in formi.io submissions
      this.studentApplications.filter((application) => {
        if (application.id === this.currentApplicationId) {
          application.submissions.filter((submission) => {
            if (submission.form === academicForm[0].id) {
              gpa = submission.data.gpa;
            }
          });
        }
      });
      return gpa;
    },
  },
  methods: {
    ...mapActions(["getProfile"]),
    ...mapMutations(["setCurrentGpa"]),
    async setStudentGpa() {
      try {
        if (!this.profileData) {
          await this.getProfile(this.getCurrentUser.participantId);
        }
        this.setCurrentGpa(this.profileData?.cumulative_gpa ?? 0);
      } catch (_) {
        this.toaster.open({
          message: "Uh-oh issue while trying to fetch your profile.",
          type: "error",
          position: "bottom",
          duration: ERROR_TIMEOUT,
        });
      }
    },
    async loadProgramData(applicationID) {
      await this.$store.dispatch("setProgramDataByApplicationId", {
        applicationId: applicationID,
        apolloClient: this.$apollo,
      });
    },
    async showGpaStatement() {
      await this.setSessionMinimumGPA();

      //if minimum gpa is null, dont show form
      if (!this.sessionMinimumGpa) {
        return false;
      }
      //If the students GPA is greater than or equal to the GPA in the program session then the student does not need to complete the gpa statement
      switch (this.currentGpa > 0) {
        case false:
          if (this.formioGpa >= this.sessionMinimumGpa) {
            return false;
          }
          break;

        default:
          if (this.currentGpa >= this.sessionMinimumGpa) {
            return false;
          }
          break;
      }

      //If the students GPA is less than the GPA in the program session then the student should see the gpa statement form.
      return true;
    },
    async showLetterOfRecommendation() {
      await this.setSessionMinimumGPA();

      //form rules requests the letter of recommendation form
      if (
        this.program.form_rules.includes("Web Form Letter of Recommendation")
      ) {
        return true;
      }

      //if there is no minumum gpa for  the program dont show the form
      if (!this.sessionMinimumGpa) {
        return false;
      }

      //If the students GPA is greater than or equal to the GPA in the program session then the student does not need to complete the letter of recommendation form.
      switch (this.currentGpa > 0) {
        case false:
          if (this.formioGpa >= this.sessionMinimumGpa) {
            return false;
          }
          break;

        default:
          if (this.currentGpa >= this.sessionMinimumGpa) {
            return false;
          }
          break;
      }

      //If the students GPA is less than the GPA in the program session then the student should see the study abroad letter of recommendation form.
      return true;
    },
    async setSessionMinimumGPA() {
      this.sessionMinimumGpa = this.session
        ? this.session?.minimum_gpa_requirement
        : await this.loadMinimumSessionGpa();
    },
    async loadMinimumSessionGpa() {
      const orders = await ordersService.list({
        participant_id: this.getCurrentUser.participantId,
        application_id: this.currentApplicationId,
        get_withdrawn: true,
      });
      const order = orders.length > 0 ? orders[0] : undefined;

      const sessionId = order?.order?.session_id;

      if (sessionId) {
        const sessionServiceResponse = await programSessionService.getProgramSession(
          {
            id: sessionId,
          }
        );
        this.session = sessionServiceResponse.data?.data?.items;
        return this.session.minimum_gpa_requirement;
      }
    },
  },
};
